import { Link } from 'react-router-dom';
import { FloatingWidget } from '@/components/FloatingWidget';
import { BrowserWarning } from '@/components/BrowserWarning';
import { UserButton } from '@/components/auth/UserButton';
import { checkBrowserSupport } from '@/services/deepgram-speech';
import { OnboardingTarget } from '@/components/onboarding';
import { useOnboarding, ONBOARDING_TOOLTIPS } from '@/hooks/useOnboarding';

export function App() {
  const { supported, reason } = checkBrowserSupport();

  // Onboarding
  const { shouldShowTooltip, dismissTooltip, skipAll, isLastTooltip } = useOnboarding();
  const showNewSessionTip = shouldShowTooltip(ONBOARDING_TOOLTIPS.NEW_SESSION);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col">
      <div className="flex-grow p-8 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
              <span className="text-white text-xl">✓</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">DemoPath</h1>
              <p className="text-sm text-gray-500">by Apparently</p>
            </div>
          </Link>
          <UserButton showName />
        </div>

        <p className="text-gray-600 mb-6">
          Never forget a talking point during your demos.
          Paste your agenda, start a session, and watch items get checked off as you speak.
        </p>

        {!supported && <BrowserWarning reason={reason} />}

        <OnboardingTarget
          tooltipId="new-session"
          tooltipTitle="Start a Session"
          tooltipDescription="Enter your talking points in the floating widget on the right, then click Start Session. Items will check off as you speak!"
          tooltipPosition="left"
          show={showNewSessionTip}
          onDismiss={() => dismissTooltip(ONBOARDING_TOOLTIPS.NEW_SESSION)}
          onSkipAll={skipAll}
          isLast={isLastTooltip(ONBOARDING_TOOLTIPS.NEW_SESSION)}
        >
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">How it works</h2>
            <ol className="list-decimal list-inside space-y-2 text-gray-600 text-sm">
              <li>Paste your talking points into the widget</li>
              <li>Click "Start Session" before your demo</li>
              <li>Position the widget where you can glance at it</li>
              <li>Items fade out as you cover them</li>
              <li>Uncovered items get highlighted over time</li>
            </ol>
          </div>
        </OnboardingTarget>

        <p className="text-sm text-gray-500">
          <strong>Tip:</strong> Drag the widget to position it in your peripheral vision
          during screenshares.
        </p>
      </div>

      {/* Footer */}
      <footer className="py-6 text-center border-t border-gray-200 bg-white/50">
        <div className="flex items-center justify-center gap-6 text-sm">
          <p className="text-gray-500">
            From the team at{' '}
            <a
              href="https://apparently.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-700 transition-colors"
            >
              apparently.dev
            </a>
          </p>
          <Link
            to="/privacy"
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            Privacy
          </Link>
        </div>
      </footer>

      {supported && <FloatingWidget />}
    </div>
  );
}
